/**
 * Shared type definitions for all assessment tools
 *
 * This module provides common types used across:
 * - Cybersecurity Maturity Assessment
 * - Compliance Readiness Checklist
 * - Interactive Ransomware Resilience Assessment (IRRA)
 * - Future Cloud Security Assessment
 *
 * By centralizing these types, we ensure consistency across all assessment tools
 * and make it easier to maintain and extend the assessment system.
 */

// ============================================================================
// Assessment Type Enum
// ============================================================================

/**
 * Enum of all assessment types in the system
 */
export type AssessmentType =
  | 'maturity'       // Cybersecurity Maturity Assessment
  | 'compliance'     // Compliance Readiness Checklist
  | 'ransomware'     // Interactive Ransomware Resilience Assessment
  | 'cloud';         // Cloud Security Assessment (future)

// ============================================================================
// Industry & Organization Types
// ============================================================================

/**
 * Industry sectors - normalized across all assessments
 *
 * Note: Some tools use variations (e.g., 'financial' vs 'finance')
 * This provides the canonical list.
 */
export type IndustrySector =
  | 'healthcare'
  | 'financial'      // Canonical: use this instead of 'finance'
  | 'technology'
  | 'legal'
  | 'manufacturing'
  | 'retail'
  | 'education'
  | 'government'
  | 'professional'   // Professional services
  | 'msp'            // Managed Service Provider
  | 'other';

/**
 * Company size ranges - standardized format
 *
 * Maturity Assessment format: '1-10', '11-50', etc.
 * Compliance Checklist format: 'small', 'medium', 'large', 'enterprise'
 *
 * This uses the more granular format from Maturity Assessment.
 */
export type CompanySize =
  | '1-10'
  | '11-50'
  | '51-100'
  | '101-250'
  | '251-500'
  | '500+';

/**
 * Company size categories for simpler classification
 */
export type CompanySizeCategory =
  | 'small'      // 1-50 employees
  | 'medium'     // 51-250 employees
  | 'large'      // 251-1000 employees
  | 'enterprise'; // 1000+ employees

/**
 * Helper function to convert granular size to category
 */
export function companySizeToCategory(size: CompanySize): CompanySizeCategory {
  switch (size) {
    case '1-10':
    case '11-50':
      return 'small';
    case '51-100':
    case '101-250':
      return 'medium';
    case '251-500':
      return 'large';
    case '500+':
      return 'enterprise';
  }
}

/**
 * Helper function to convert category to representative granular size
 */
export function companyCategoryToSize(category: CompanySizeCategory): CompanySize {
  switch (category) {
    case 'small':
      return '11-50';
    case 'medium':
      return '101-250';
    case 'large':
      return '251-500';
    case 'enterprise':
      return '500+';
  }
}

// ============================================================================
// Compliance Framework Types
// ============================================================================

/**
 * All compliance frameworks supported across assessments
 */
export type ComplianceFramework =
  | 'hipaa'       // Health Insurance Portability and Accountability Act
  | 'soc2'        // Service Organization Control 2
  | 'pci_dss'     // Payment Card Industry Data Security Standard
  | 'iso27001'    // ISO/IEC 27001 Information Security Management
  | 'nist_csf'    // NIST Cybersecurity Framework
  | 'gdpr'        // General Data Protection Regulation
  | 'cmmc'        // Cybersecurity Maturity Model Certification
  | 'state_privacy' // State privacy laws (CCPA, etc.)
  | 'nist800171'  // NIST 800-171 (federal contractors)
  | 'fedramp'     // Federal Risk and Authorization Management Program
  | 'none';       // No specific framework

/**
 * Framework metadata for display and documentation
 */
export interface FrameworkMetadata {
  id: ComplianceFramework;
  name: string;
  shortName: string;
  description: string;
  targetAudience: string;
  controlCount?: number;
  color?: string;
  website?: string;
}

/**
 * Standard framework metadata - extend as needed
 */
export const FRAMEWORK_METADATA: Partial<Record<ComplianceFramework, FrameworkMetadata>> = {
  hipaa: {
    id: 'hipaa',
    name: 'Health Insurance Portability and Accountability Act',
    shortName: 'HIPAA',
    description: 'US healthcare data protection and privacy standard',
    targetAudience: 'Healthcare providers, covered entities, business associates',
    controlCount: 150,
    color: '#0ea5e9',
    website: 'https://www.hhs.gov/hipaa/',
  },
  soc2: {
    id: 'soc2',
    name: 'Service Organization Control 2 Type II',
    shortName: 'SOC 2',
    description: 'Trust services criteria for service organizations',
    targetAudience: 'SaaS companies, technology service providers',
    controlCount: 180,
    color: '#8b5cf6',
    website: 'https://www.aicpa.org/soc',
  },
  pci_dss: {
    id: 'pci_dss',
    name: 'Payment Card Industry Data Security Standard',
    shortName: 'PCI-DSS',
    description: 'Security standard for payment card data handling',
    targetAudience: 'Organizations handling payment card data',
    controlCount: 350,
    color: '#ef4444',
    website: 'https://www.pcisecuritystandards.org/',
  },
  iso27001: {
    id: 'iso27001',
    name: 'ISO/IEC 27001:2022',
    shortName: 'ISO 27001',
    description: 'International information security management standard',
    targetAudience: 'Enterprises, international organizations',
    controlCount: 93,
    color: '#10b981',
    website: 'https://www.iso.org/standard/82875.html',
  },
  nist_csf: {
    id: 'nist_csf',
    name: 'NIST Cybersecurity Framework 2.0',
    shortName: 'NIST CSF',
    description: 'US cybersecurity framework for critical infrastructure',
    targetAudience: 'Government contractors, critical infrastructure',
    controlCount: 106,
    color: '#f59e0b',
    website: 'https://www.nist.gov/cyberframework',
  },
  gdpr: {
    id: 'gdpr',
    name: 'General Data Protection Regulation',
    shortName: 'GDPR',
    description: 'EU data protection and privacy regulation',
    targetAudience: 'Organizations handling EU citizen data',
    controlCount: 99,
    color: '#3b82f6',
    website: 'https://gdpr.eu/',
  },
  cmmc: {
    id: 'cmmc',
    name: 'Cybersecurity Maturity Model Certification',
    shortName: 'CMMC',
    description: 'DoD cybersecurity standard for defense contractors',
    targetAudience: 'Defense contractors, DoD supply chain',
    controlCount: 171,
    color: '#6366f1',
    website: 'https://dodcio.defense.gov/CMMC/',
  },
};

// ============================================================================
// Assessment Profile Types
// ============================================================================

/**
 * Base profile interface that all assessments can extend
 * Contains common organizational information
 */
export interface AssessmentProfile {
  // Organization details
  companyName?: string;
  industry: IndustrySector;
  companySize: CompanySize;

  // Regulatory/compliance context
  regulatoryRequirements: ComplianceFramework[];

  // Budget (optional, used for ROI calculations)
  annualCybersecurityBudget?: number;

  // Data handling flags
  handlesPaymentData?: boolean;
  handlesPHI?: boolean;  // Protected Health Information
  handlesPII?: boolean;  // Personally Identifiable Information

  // Current state
  currentStatus?: 'none' | 'in_progress' | 'certified';
  targetDate?: string;  // ISO date
  hasExistingPolicies?: boolean;
}

/**
 * Extended profile for maturity assessments
 */
export interface MaturityAssessmentProfile extends AssessmentProfile {
  maturityTargetLevel?: 1 | 2 | 3 | 4 | 5;
}

/**
 * Extended profile for compliance assessments
 */
export interface ComplianceAssessmentProfile extends AssessmentProfile {
  targetFrameworks: ComplianceFramework[];
}

/**
 * Extended profile for ransomware/resilience assessments
 */
export interface ResilienceAssessmentProfile extends AssessmentProfile {
  hasAirGappedBackups?: boolean;
}

// ============================================================================
// Assessment Step/Wizard Types
// ============================================================================

/**
 * Common wizard step types across assessments
 */
export type AssessmentStep =
  | 'introduction'   // Welcome/overview screen
  | 'profile'        // Company profile collection
  | 'assessment'     // Question flow
  | 'results';       // Results display

/**
 * Assessment state for wizard flow
 */
export interface AssessmentState<TProfile extends AssessmentProfile = AssessmentProfile> {
  step: AssessmentStep;
  profile: TProfile | null;
  responses: Record<string, unknown>;
  currentCategory?: string;
  currentQuestionIndex: number;
  startedAt: string | null;
  completedAt: string | null;
}

// ============================================================================
// Progress Persistence Types
// ============================================================================

/**
 * Common structure for localStorage persistence
 */
export interface SavedProgress<TProfile extends AssessmentProfile = AssessmentProfile> {
  version: string;                    // Schema version for migration
  assessmentType: AssessmentType;
  profile: TProfile | null;
  answers: Record<string, unknown>;
  currentIndex: number;
  currentCategory?: string;
  showResults: boolean;
  startedAt: string | null;
  completedAt: string | null;
  savedAt: string;                    // ISO timestamp
}

/**
 * Storage key generator for consistent naming
 */
export function getStorageKey(assessmentType: AssessmentType, context?: string): string {
  const base = `assessment-${assessmentType}`;
  return context ? `${base}:${context}` : base;
}

// ============================================================================
// Question & Answer Types
// ============================================================================

/**
 * Common question types
 */
export type QuestionType =
  | 'single'         // Single choice (radio buttons)
  | 'multiple'       // Multiple choice (checkboxes)
  | 'number'         // Numeric input
  | 'text'           // Text input
  | 'scale';         // Likert scale

/**
 * Common answer values
 */
export type AnswerValue =
  | 'yes'
  | 'no'
  | 'partial'
  | 'na'             // Not applicable
  | 'unknown';       // Don't know

/**
 * Priority/severity levels
 */
export type PriorityLevel =
  | 'critical'
  | 'high'
  | 'medium'
  | 'low';

/**
 * Risk levels
 */
export type RiskLevel =
  | 'critical'
  | 'high'
  | 'medium'
  | 'low'
  | 'very_high'
  | 'very_low';

/**
 * Maturity levels (1-5 scale)
 */
export type MaturityLevel = 1 | 2 | 3 | 4 | 5;

/**
 * Maturity level names
 */
export const MATURITY_LEVEL_NAMES: Record<MaturityLevel, string> = {
  1: 'Initial/Unstructured',
  2: 'Managed/Repeatable',
  3: 'Defined/Standardized',
  4: 'Quantitatively Managed',
  5: 'Optimizing/Adaptive',
};

/**
 * Maturity level descriptions
 */
export const MATURITY_LEVEL_DESCRIPTIONS: Record<MaturityLevel, string> = {
  1: 'Security is ad-hoc and reactive. Controls are inconsistent and rely on individual effort.',
  2: 'Foundational security controls are documented and can be repeated. Policies exist and are followed.',
  3: 'Security processes are standardized across the organization. Comprehensive program with executive oversight.',
  4: 'Security effectiveness is continuously measured. Metrics drive decisions and improvements.',
  5: 'Automation, threat intelligence, and continuous optimization. Industry-leading security posture.',
};

// ============================================================================
// Scoring & Results Types
// ============================================================================

/**
 * Score band for categorizing results
 */
export interface ScoreBand {
  min: number;
  max: number;
  label: string;
  color: string;
  description: string;
}

/**
 * Common readiness/maturity bands (0-100 scale)
 */
export const READINESS_BANDS: ScoreBand[] = [
  {
    min: 0,
    max: 20,
    label: 'Not Ready',
    color: 'red',
    description: 'Significant work required before certification audit',
  },
  {
    min: 21,
    max: 40,
    label: 'Early Stage',
    color: 'orange',
    description: 'Foundation in place but major gaps remain',
  },
  {
    min: 41,
    max: 60,
    label: 'In Progress',
    color: 'yellow',
    description: 'Good progress, address remaining gaps',
  },
  {
    min: 61,
    max: 80,
    label: 'Nearly Ready',
    color: 'green',
    description: 'Minor gaps to address before audit',
  },
  {
    min: 81,
    max: 100,
    label: 'Audit Ready',
    color: 'blue',
    description: 'Well-prepared for certification audit',
  },
];

/**
 * Common maturity bands (0-100 scale)
 */
export const MATURITY_BANDS: ScoreBand[] = [
  {
    min: 0,
    max: 20,
    label: 'Level 1: Unstructured',
    color: 'red',
    description: 'Ad-hoc processes; objectives undefined and unknown',
  },
  {
    min: 21,
    max: 40,
    label: 'Level 2: Repeatable',
    color: 'orange',
    description: 'Basic controls exist, but objectives are rarely validated',
  },
  {
    min: 41,
    max: 60,
    label: 'Level 3: Standardized',
    color: 'yellow',
    description: 'Documented strategy in place; objectives defined',
  },
  {
    min: 61,
    max: 80,
    label: 'Level 4: Managed',
    color: 'green',
    description: 'Regular testing validates objectives with leadership oversight',
  },
  {
    min: 81,
    max: 100,
    label: 'Level 5: Optimized',
    color: 'blue',
    description: 'Continuous assurance with near-optimal performance',
  },
];

/**
 * Gap/finding severity
 */
export interface GapSeverity {
  level: PriorityLevel;
  label: string;
  color: string;
}

/**
 * Common gap severities
 */
export const GAP_SEVERITIES: Record<PriorityLevel, GapSeverity> = {
  critical: {
    level: 'critical',
    label: 'Critical',
    color: 'rose',
  },
  high: {
    level: 'high',
    label: 'High',
    color: 'amber',
  },
  medium: {
    level: 'medium',
    label: 'Medium',
    color: 'yellow',
  },
  low: {
    level: 'low',
    label: 'Low',
    color: 'slate',
  },
};

// ============================================================================
// Remediation Types
// ============================================================================

/**
 * Effort estimates
 */
export type EffortEstimate =
  | 'hours'
  | 'days'
  | 'weeks'
  | 'months';

/**
 * Cost estimates
 */
export type CostEstimate =
  | 'free'
  | 'low'      // < $5k
  | 'medium'   // $5k - $25k
  | 'high';    // > $25k

/**
 * Remediation guidance structure
 */
export interface RemediationGuidance {
  problem: string;
  fix: string;
  resources: Array<{
    label: string;
    href: string;
  }>;
  estimatedEffort: EffortEstimate;
  estimatedCost: CostEstimate;
}

/**
 * Remediation item in a roadmap
 */
export interface RemediationItem {
  rank: number;
  questionId: string;
  title: string;
  description: string;
  priority: PriorityLevel;
  effort: EffortEstimate | string;
  cost: CostEstimate | string;
  frameworkImpact?: ComplianceFramework[];
  controlReferences?: string[];
}

// ============================================================================
// Time & Cost Estimation Types
// ============================================================================

/**
 * Three-point time estimate
 */
export interface TimeEstimate {
  optimistic: number;   // weeks
  realistic: number;    // weeks
  conservative: number; // weeks
}

/**
 * Cost range estimate
 */
export interface CostRange {
  low: number;
  high: number;
  currency?: string;  // Default: USD
}

/**
 * ROI calculation
 */
export interface ROICalculation {
  investment: number;
  breachCostAvoidance: number;
  revenueProtection: number;
  netROI: number;
  paybackPeriod: string;
}

// ============================================================================
// Utility Types
// ============================================================================

/**
 * Helper to find score band for a given score
 */
export function getScoreBand(score: number, bands: ScoreBand[]): ScoreBand {
  return bands.find(band => score >= band.min && score <= band.max) ?? bands[0];
}

/**
 * Helper to format score as percentage
 */
export function formatScore(score: number, decimals: number = 0): string {
  return `${score.toFixed(decimals)}%`;
}

/**
 * Helper to get Tailwind color classes for a score
 */
export function getScoreColorClasses(score: number | null): string {
  if (score === null) {
    return 'bg-slate-50 border-slate-200 text-slate-600';
  }
  if (score >= 75) {
    return 'bg-emerald-50 border-emerald-200 text-emerald-700';
  }
  if (score >= 45) {
    return 'bg-amber-50 border-amber-200 text-amber-700';
  }
  return 'bg-rose-50 border-rose-200 text-rose-700';
}

/**
 * Helper to validate assessment progress for saving
 */
export function validateSavedProgress<T extends AssessmentProfile>(
  data: unknown
): data is SavedProgress<T> {
  if (!data || typeof data !== 'object') {
    return false;
  }

  const progress = data as Partial<SavedProgress>;

  return !!(
    progress.version &&
    progress.assessmentType &&
    progress.answers &&
    typeof progress.currentIndex === 'number' &&
    typeof progress.showResults === 'boolean'
  );
}
